/* eslint-disable react/display-name */
import { Alert, AlertIcon, Box, Center, Flex, Spinner, Text } from '@chakra-ui/react';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { djs } from '@duke-hq/djs';
import React from 'react';
import { useParams } from 'react-router-dom';
import { Header } from './Header';
import { StudentCourseEnrolmentStatus } from '@duke-hq/svc-api-db';
import CourseItemsStatusAccordion from '@duke-hq/enrolments-ui/components/CourseItemsStatusAccordion/CourseItemsStatusAccordion';

export const EnrolmentDetails: React.FC<{ onClose?: () => void }> = React.memo(({ onClose }) => {
  const { enrolmentId } = useParams<{ enrolmentId: string }>();
  const {
    data: courseEnrolment,
    isLoading,
    error,
  } = trpcTenant.enrolments.byIdWithResourceStatus.useQuery(
    {
      enrolmentId: Number(enrolmentId),
    },
    { enabled: Number.isInteger(Number(enrolmentId)) },
  );

  const { data: assessmentsInRevision } = trpcTenant.enrolments.assessmentsInRevision.useQuery(
    {
      courseEnrolmentId: Number(enrolmentId),
    },
    { enabled: Number.isInteger(Number(enrolmentId)) },
  );

  if (isLoading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" aria-label={t`Loading enrolment details`} />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <Trans>An error occurred while loading the enrolment. Please try again later.</Trans>
      </Alert>
    );
  }

  if (!courseEnrolment) {
    return (
      <Alert>
        <Trans>Enrolment not found</Trans>
      </Alert>
    );
  }

  return (
    <>
      <Header
        assessmentsInRevision={assessmentsInRevision}
        enrolment={courseEnrolment}
        onClose={onClose}
      />
      {courseEnrolment.status === StudentCourseEnrolmentStatus.WITHDRAWN &&
      courseEnrolment.withdrawn_at ? (
        <Box>
          <Alert bg="bg.muted" justifyContent="space-between" alignItems="center">
            <Flex alignItems="center">
              <AlertIcon color="fg.emphasis" />
              <Text color="fg.emphasis" fontSize="sm">
                <Trans>This enrolment was withdrawn on the</Trans>{' '}
                {djs(courseEnrolment.withdrawn_at).format('D MMM YYYY')}
              </Text>
            </Flex>
          </Alert>
        </Box>
      ) : null}
      <Flex align="start" direction="column" overflow="scroll">
        <CourseItemsStatusAccordion
          assessmentsInRevision={assessmentsInRevision}
          courseEnrolment={courseEnrolment}
          labels={{
            semester: (n: number) => t`Semester ${n}`,
            lln: t`Language, Literacy, and Numeracy`,
            skillSetUnits: t`Skill set units`,
            enrolled: t`Enrolled`,
            notEnrolled: t`Not Enrolled`,
            unit: t`Unit`,
            releaseDate: t`Release date`,
            status: t`Status`,
          }}
        />
      </Flex>
    </>
  );
});
